import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Grid, Box, FormHelperText } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, FieldValues } from 'react-hook-form';
import {
  getJobById,
  ICandidate,
  newMappingEntity,
  updateCandidateSubmission,
  uploadFileToS3,
} from 'wisestep-api';
import {
  WsTypography,
  Button,
  IUserDTO,
  getCurrentZoneAbbr,
  getDateUsingFormat,
  getStandardTime,
  WsTypoVariants,
  WsTypoColors,
  WsSelect,
  WsSelectObj,
  ControlledTextArea,
  WsDatePicker,
  Label,
  WsDateTimePicker,
  WsFileUpload,
  WsColors,
} from 'wisestep-base';

import { getFieldFromSelectedStatus } from '../../../../components/UpdateStatusModal/helper';
import OfferedPayRateField from '../../../../components/UpdateStatusModal/OfferedPayRateField';
import jobCandidateInteraction from '../../../../components/UpdateStatusModal/payload';
import { allowedContractTypes } from '../../../../components/UpdateStatusModal/UpdateStatusModal';
import { useUserContext } from '../../../../context';
import {
  downloadCV,
  ITrackCvPages,
  trackCV,
} from '../../../../features/conversationWidget/utils/cvHelper';
import { stageStatusPipelineType } from '../../../../features/crmSettings/customizeStageStatus/helper';
import { useListingSettings } from '../../../../features/jobs/listing/hooks';
import {
  getCandidateStageData,
  getStageStatusSettings,
} from '../../../../utils/CandidateStageStatusHelper';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import {
  IStageOption,
  IStatuses,
  formLabel,
  updateStageStateFormDefaultValues,
  updateJobCandidateInteraction,
  stageStatusFormSchema,
} from '../../helper/stageStatusHelper';
import useGetSubmissionOtherDocs from '../hooks/useGetSubmissionOtherDocs';

interface IStageStatusDialogToolTip {
  closeDialog: () => void;
  candidate: ICandidate;
  userStageStatus: [];
  updateUI: (candidate: ICandidate) => void;
}

interface IActionUrl {
  action: string;
  url: string | null;
}

const StageStatusDialogToolTip = ({
  closeDialog,
  candidate,
  userStageStatus,
  updateUI,
}: IStageStatusDialogToolTip) => {
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [stagesData, setStagesData] = useState<stageStatusPipelineType[]>([]);
  const loggedInUserName = loggedInUser.firstName ?? loggedInUser.lastName ?? loggedInUser.email;
  const [stageOptions, setStageOptions] = useState<WsSelectObj[]>();
  const [statusOptions, setStatusOptions] = useState<WsSelectObj[]>();
  const [mandatoryFields, setMandatoryFields] = useState<string[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { jobFieldSettings } = useListingSettings();
  const [jobType, setJobType] = useState('');
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const { otherDocuments, setOtherDocuments } = useGetSubmissionOtherDocs(candidate);
  const [candidateInfo, setCandidate] = useState<ICandidate | null>(candidate);

  const methods = useForm({
    resolver: yupResolver(stageStatusFormSchema(mandatoryFields)),
    defaultValues:
      jobFieldSettings && updateStageStateFormDefaultValues(candidateInfo, jobFieldSettings),
  });

  const { handleSubmit, watch, reset, setValue } = methods;

  const selectedStage = watch('applicationStage');
  const selectedStatus = watch('applicationState');

  useEffect(() => {
    const fields = getFieldFromSelectedStatus(stagesData, selectedStage, selectedStatus);
    setMandatoryFields(fields);
    if (
      candidateInfo?.applicationStage !== selectedStage ||
      candidateInfo?.applicationState !== selectedStatus
    ) {
      setValue('stageDate', null);
      setValue('joiningDate', null);
    }
  }, [candidateInfo, selectedStage, selectedStatus, setValue, stagesData]);

  const onFormSubmit = (data: FieldValues) => {
    candidateInfo && update(candidateInfo, data);
  };

  useEffect(() => {
    (async () => {
      const response = await getStageStatusSettings();
      setStagesData(getCandidateStageData(response?.data));
      const jobReq = await getJobById(candidateInfo?.jobId ?? '');
      if (jobReq?.data?.jobType) {
        setJobType(jobReq?.data?.jobType);
      }
    })();
  }, [candidateInfo?.jobId]);

  const isJobContract = allowedContractTypes.includes(jobType);

  const update = async (conversation: ICandidate, data: FieldValues) => {
    if (isEmpty(data.applicationStage) || isEmpty(data.applicationState)) {
      setNotification(warning('Please update the Stage and Status'));
      return;
    }
    setIsSubmit(true);
    let editedNote = '';
    const endingNote = ` with Stage Date ${getDateUsingFormat(
      data.stageDate,
      'DD MMMM, YYYY, h:mm a'
    )} ${getStandardTime()} - UTC ${getCurrentZoneAbbr()}`;

    if (
      (!isEmpty(conversation.applicationStage) &&
        data.applicationStage !== conversation.applicationStage) ||
      (!isEmpty(conversation.applicationState) &&
        data.applicationState !== conversation.applicationState)
    ) {
      editedNote = `The Candidate Status has now been updated from <strong>
                    ${conversation.applicationStage}  </strong> and <strong>
                    ${conversation.applicationState}  </strong> to <strong>
                    ${data.applicationStage}  </strong> and <strong>
                    ${data.applicationState}  </strong>`;
    } else {
      editedNote = `The Candidate Status is now  <strong>
                    ${data.applicationStage}  </strong> and <strong>
                    ${data.applicationState}`;
    }
    editedNote = `${editedNote}. ${!isEmpty(endingNote) ? endingNote : ''}`;
    if (data.candidateNotes && !isEmpty(data.candidateNotes)) {
      editedNote = `${editedNote} <br/><br/> ${loggedInUserName} has added the following note : ${data.candidateNotes}`;
    }
    if (data.joiningDate && !isEmpty(String(data.joiningDate))) {
      editedNote = `${editedNote} <br/> Joining Date : ${getDateUsingFormat(
        data.joiningDate,
        'DD MMMM, YYYY'
      )}`;
    }
    updateJobCandidateInteraction(
      conversation.id,
      data,
      editedNote,
      loggedInUserName,
      jobCandidateInteraction,
      otherDocuments
    );
    // const response = await updateJobCandidateNotesStateStage(jobCandidateInteraction);
    const response = await updateCandidateSubmission([jobCandidateInteraction]);
    reset();
    if (response) {
      updateUI(response?.data?.[0]);
      setNotification(success('Submission Updated'));
    } else {
      setNotification(failed('Update failed'));
    }
    closeDialog();
    setIsSubmit(false);
  };

  const onUpload = async (file: File[], field: string) => {
    const url = await uploadFileToS3(file[0]);
    if (field === 'document') {
      const alteredCandidate = (candidateInfo as ICandidate) ?? newMappingEntity();
      const alteredDocuments = [...otherDocuments];
      alteredDocuments.push({
        documentLocation: url,
        originalName: file[0].name,
        tag: null,
        uploadedOn: new Date(),
      });
      setOtherDocuments([...alteredDocuments]);
      alteredCandidate.otherDocuments = alteredDocuments;
      setCandidate({ ...alteredCandidate });
    }
  };
  const resumeAction = (actionUrl: IActionUrl) => {
    switch (actionUrl.action) {
      case 'view':
        !isEmpty(actionUrl.url) && trackCV('view', actionUrl.url, ITrackCvPages.SUBMISSION);
        break;
      case 'removeCandidate':
        if (candidateInfo) {
          const alteredCandidate = { ...candidateInfo };
          alteredCandidate.resumeFileOriginalName = '';
          alteredCandidate.resumeLocation = '';
          alteredCandidate.resumeUploadedOn = null;
          setCandidate({ ...alteredCandidate });
        }
        break;
      case 'removeOtherDocument':
        if (candidateInfo) {
          const alteredCandidate = { ...candidateInfo };
          alteredCandidate.otherDocuments = alteredCandidate?.otherDocuments?.filter(
            (document) => document?.documentLocation != actionUrl.url
          );
          alteredCandidate?.otherDocuments && setOtherDocuments(alteredCandidate?.otherDocuments);
          setCandidate({ ...alteredCandidate });
        }
        break;
    }
  };

  const onReset = () => {
    closeDialog();
    reset();
  };

  useEffect(() => {
    const stage: WsSelectObj[] = userStageStatus
      ?.filter((stageAndStatus: IStageOption) => !stageAndStatus?.isArchive)
      ?.map((stateStatus: IStageOption) => {
        return { value: stateStatus?.stage ?? '', label: stateStatus?.stage ?? '' };
      });
    setStageOptions(stage);
  }, [userStageStatus]);

  useEffect(() => {
    userStageStatus.map((stateStatus: IStageOption) => {
      if (selectedStage === stateStatus?.stage) {
        const stage: WsSelectObj[] = stateStatus.statuses
          .filter((status: IStatuses) => status.status === 'active')
          .map((status) => {
            return { value: status?.value ?? '', label: status?.value ?? '' };
          });
        setStatusOptions(stage);
      }
    });
  }, [selectedStage, userStageStatus]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)} onReset={onReset}>
        <Grid
          container
          spacing={2}
          xs={11.5}
          sx={{
            margin: '0px',
          }}
        >
          <Grid item xs={12}>
            <Box py={1}>
              <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK} bold>
                Update Stage And Status
              </WsTypography>
            </Box>
            <WsSelect
              name="applicationStage"
              label="Stage"
              selectArray={stageOptions ?? []}
              required={true}
            />
          </Grid>
          <Grid item xs={12}>
            <WsSelect
              name="applicationState"
              label="Status"
              selectArray={statusOptions ?? []}
              required={true}
            />
          </Grid>
          <Grid item xs={12}>
            {mandatoryFields.includes('Stage Date & Time') ? (
              <>
                <Label>
                  {formLabel('Stage Date & Time', mandatoryFields.includes('Stage Date & Time'))}
                </Label>
                <WsDateTimePicker
                  name="stageDate"
                  label=""
                  disablePast={false}
                  popperProps={{ placement: 'right-end' }}
                />
              </>
            ) : (
              <>
                <Label>{formLabel('Stage Date', mandatoryFields.includes('Stage Date'))}</Label>
                <WsDatePicker name="stageDate" label="" disablePast={false} />
              </>
            )}
          </Grid>
          {mandatoryFields.includes('Offer Payrate') && (
            <Grid ml={2}>
              <Label>
                {(isJobContract ? 'Offered Pay Rate' : 'Offered Salary') +
                  (mandatoryFields.includes('Offer Payrate') ? ' *' : '')}
              </Label>
              <OfferedPayRateField name="offeredPayRate" />
            </Grid>
          )}
          {mandatoryFields.includes('Joining Date') && (
            <Grid item xs={12}>
              <Label>{formLabel('Joining Date', true)}</Label>
              <WsDatePicker name="joiningDate" label="" disablePast={false} required />
            </Grid>
          )}
          <Grid item xs={12}>
            <Label>{formLabel('Comments', mandatoryFields.includes('Comments'))}</Label>
            <ControlledTextArea name="candidateNotes" labelText="" showMandatorySymbol={false} />
          </Grid>
          <Grid item xs={12}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
              {`Other Documents ${mandatoryFields.includes('Other Documents') ? '*' : ''}`}
            </WsTypography>
            <WsFileUpload
              multiple={false}
              onUpload={(file: File[]) => onUpload && onUpload(file, 'document')}
              name={'document'}
              placeholderText=" "
              bgRequired={false}
              buttonText={'Upload Other Files'}
              maxFileSize={3}
            />
            {isSubmit && mandatoryFields.includes('Other Documents') && isEmpty(otherDocuments) && (
              <FormHelperText error>Please upload document</FormHelperText>
            )}
            {candidateInfo?.otherDocuments?.map((document, index: number) => (
              <Grid key={index} direction="row" spacing={2} mb={2}>
                <WsTypography variant={WsTypoVariants.BODY1}>
                  {document?.originalName ?? ''}
                </WsTypography>
                <DownloadIcon
                  onClick={() =>
                    downloadCV(
                      document?.documentLocation?.split('?')[0] ?? '',
                      document?.originalName ?? ''
                    )
                  }
                  sx={{
                    color: WsColors.TheBlues500,
                    fontSize: 24,
                    cursor: 'pointer',
                  }}
                />
                <VisibilityIcon
                  onClick={() =>
                    resumeAction &&
                    resumeAction({
                      action: 'view',
                      url: document?.documentLocation ?? '',
                    })
                  }
                  sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                />

                <CloseIcon
                  onClick={() =>
                    resumeAction &&
                    resumeAction({
                      action: 'removeOtherDocument',
                      url: document?.documentLocation ?? '',
                    })
                  }
                  sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                />
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" type="reset">
              Close
            </Button>
            <Button sx={{ marginLeft: 2 }} variant="contained" type="submit">
              Save
            </Button>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};
export default StageStatusDialogToolTip;
